import React, { useState, useEffect} from 'react'
import { Box, Typography, Card, CardContent } from '@mui/material'
import DevicesIcon from '@mui/icons-material/Devices'
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid'
import UAParser from 'ua-parser-js'
import { formatDistanceToNow } from 'date-fns'
import { es, ptBR, enUS } from 'date-fns/locale'
import { useGetIdentity, useTranslate, useNotify } from 'react-admin'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import LogoutIcon from '@mui/icons-material/Logout'
import LoadingButton from '@mui/lab/LoadingButton'
import { userServices } from 'src/services/auth/user/user.service'
import { Skeleton } from '@mui/material'
import { ConfirmationDialog } from 'src/components/uiElements/styledComponents/ConfirmationDialog'

const getLocale = (language) => {
  const locales = {
    'es-AR': es,
    'en-US': enUS,
    'pt-BR': ptBR,
  }
  return locales[language] || es 
}

const MyDevices = () => {
    const { identity } = useGetIdentity()
    const translate = useTranslate()
    const [devices, setDevices] = useState([])
    const [loadingDevices, setLoadingDevices] = useState(false)
    const [openLogoutModal, setOpenLogoutModal] = useState(false)
    const [confirmLogout, setConfirmLogout] = useState(false)
    const [selectedDevice, setSelectedDevice] = useState(null)
    const [loadingUpdateDevice, setLoadingUpdateDevice] = useState(false)
    const notify = useNotify()

    const getDevicesByUser = async (user_id) => {
      try {
        setLoadingDevices(true)
        const devices = await userServices.devicesByUserFilter({ user_id })
        if (devices){
          setDevices(devices)
        }
      } catch (error) {
        setDevices([])
      }
      finally {
        setLoadingDevices(false)
      }
    }

    const removeDeviceByUser = async (device) => {
      try {
        setLoadingUpdateDevice(true)
        const status = await userServices.removeDevicesByUser(device.id)
        if (status >= 200 && status <= 204) {
          notify('ra.message.device_disabled_success', {
            type: 'success',
          })
        }
      } catch (error) {
        notify('ra.message.device_disabled_error', {
          type: 'error',
        })
      } finally {
        setLoadingUpdateDevice(false)
        setOpenLogoutModal(false)
        getDevicesByUser(identity?.user_id)
      }
    }

    useEffect(() => {
      if (identity?.user_id) {
        getDevicesByUser(identity?.user_id)
      } 
    }, [identity?.user_id])
  
    useEffect(() => {
      if (confirmLogout) {
        removeDeviceByUser(selectedDevice)
      }
    }, [confirmLogout])

    const getDeviceName = (device) => {
      try {
        const parser = new UAParser(device)
        const deviceName = parser.getOS().name
        const browserName = parser.getBrowser().name
        return `${deviceName}(${browserName})`
      } catch (error) {
        return ''
      }
    }

    const convertToTimeZone = (dateString, timeZone) => {
      const date = new Date(dateString + 'Z') // lo pasamos a UTC
      return new Date(
        new Intl.DateTimeFormat('en-US', {
          timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }).format(date)
      )
    }

    const formatLastLogin = (lastLogin, language) => {
      const timeZone = localStorage.getItem('timezone') ?? Intl.DateTimeFormat().resolvedOptions().timeZone//process.env.REACT_APP_TIME_ZONE || 'America/Argentina/Buenos_Aires'
      const localLastLogin = convertToTimeZone(lastLogin, timeZone) // lo pasamos a hora arg(esta en US)

      return formatDistanceToNow(localLastLogin, { addSuffix: true, locale: getLocale(language) })
    }

    const sortedDevices = devices.sort((a, b) => {
     try {
       const isCurrentA = a.name === identity?.visitor_id ? 1 : 0
       const isCurrentB = b.name === identity?.visitor_id ? 1 : 0

       // obtener el dispositivo actual para mostrarlo primero
       if (isCurrentA !== isCurrentB) {
         return isCurrentB - isCurrentA
       }

       // si ninguno o ambos son el actual, ordénalos por `last_login` (más reciente primero).
       return new Date(b.last_login) - new Date(a.last_login)
     } catch (error) {
        return []
     }
    })

    return (
      <>
        <Box padding={3} maxWidth={800} margin='0 auto'>
          <Typography variant='h6' gutterBottom>
            {translate('ra.message.my_devices_duration_message')}
          </Typography>
          {loadingDevices
            ? Array(3)
                .fill()
                .map((_, index) => (
                  <Card
                    key={index}
                    variant='outlined'
                    sx={{ maxWidth: 600, margin: '20px auto', borderRadius: '8px' }}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        width={'10%'}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: 1,
                        }}
                      >
                        <Skeleton variant='circular' width={40} height={40} />
                      </Box>
                      <Box width={'80%'}>
                        <Typography variant='subtitle2'>
                          <Skeleton width='60%' />
                        </Typography>
                        <Typography variant='body2' color='textSecondary'>
                          <Skeleton width='40%' />
                        </Typography>
                        <Typography variant='body2' color='textSecondary'>
                          <Skeleton width='50%' />
                        </Typography>
                      </Box>
                      <Box width={'10%'} display={'flex'} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Skeleton variant='rectangular' width={30} height={30} />
                      </Box>
                    </CardContent>
                  </Card>
                ))
            : sortedDevices.map((device) => {
                const parser = new UAParser(device.user_agent)
                const deviceType = parser.getDevice().type || 'computer'
                const deviceName = parser.getOS().name || translate('ra.field.unknown')
                const browserName = parser.getBrowser().name || translate('ra.field.unknown')
                const currentSession = device.name === identity?.visitor_id
                const last_login = formatLastLogin(device.last_login, identity?.language)

                return (
                  <Card
                    key={device.id}
                    variant='outlined'
                    sx={{ maxWidth: 600, margin: '20px auto', borderRadius: '8px' }}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        width={'10%'}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: 1,
                        }}
                      >
                        {deviceType === 'computer' ? (
                          <DevicesIcon fontSize='large' />
                        ) : (
                          <PhoneAndroidIcon fontSize='large' />
                        )}
                      </Box>
                      <Box width={'80%'}>
                        <Typography variant='subtitle2'>
                          {translate('ra.message.one_session_active')}{' '}
                          {deviceType === 'computer'
                            ? translate('ra.field.my_device_computer')
                            : translate('ra.field.my_device_phone')}{' '}
                          {deviceName}
                        </Typography>
                        <Typography variant='body2' color='textSecondary'>
                          {translate('ra.field.browser')}: {browserName}
                        </Typography>
                        {!currentSession && (
                          <Typography variant='body2' color='textSecondary'>
                            {translate('ra.field.last_active')}: {last_login}
                          </Typography>
                        )}
                        {currentSession && (
                          <Typography
                            variant='body2'
                            color='primary'
                            sx={{ display: 'flex', alignItems: 'center', marginTop: 2 }}
                          >
                            <Box
                              component='span'
                              sx={{ display: 'flex', alignItems: 'center', marginRight: 1 }}
                            >
                              <CheckCircleIcon fontSize='small' />
                            </Box>
                            {translate('ra.message.your_current_session')}
                          </Typography>
                        )}
                      </Box>
                      <Box width={'10%'} display={'flex'} sx={{ display: 'flex', alignItems: 'center' }}>
                        {!currentSession && (
                          <LoadingButton
                            size='small'
                            variant='text'
                            color='error'
                            startIcon={<LogoutIcon />}
                            title={translate('ra.auth.logout')}
                            onClick={() => {
                              setSelectedDevice(device)
                              setOpenLogoutModal(true)
                            }}
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                )
              })}
        </Box>
        {openLogoutModal && (
          <ConfirmationDialog
            title={translate('ra.message.logout_device', { device: getDeviceName(selectedDevice?.user_agent) })}
            buttonConfirmText={translate('ra.message.yes')}
            buttonCancel={translate('ra.message.no')}
            setOpen={setOpenLogoutModal}
            setConfirm={setConfirmLogout}
            open={openLogoutModal}
            loadingModalButton={loadingUpdateDevice}
          />
        )}
      </>
    )
}



export default MyDevices
